import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { DatePipe, DecimalPipe, NgFor, NgIf } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { ApiUrlService } from '../../core/api-url.service';

@Component({
  selector: 'app-home-page',
  standalone: true,
  imports: [NgIf, NgFor, DecimalPipe, DatePipe],
  templateUrl: './home.page.html',
  styleUrl: './home.page.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class HomePageComponent implements OnInit {
  home: HomeResponse | null = null;
  lastPurchase: PurchaseHistoryItem | null = null;
  isLoading = false;
  isLastPurchaseLoading = false;
  isConfirming = false;
  isWarningSending = false;
  errorMessage = '';
  successMessage = '';
  isHistoryOpen = false;
  showConfirmPurchase = false;
  showConfirmWarning = false;
  historyPurchases: PurchaseHistoryItem[] = [];
  historyErrorMessage = '';
  isHistoryLoading = false;
  isHistoryLoadingMore = false;
  historyLimit = 50;
  historyHasMore = true;
  readonly stockStatusLabels: Record<StockStatus, string> = {
    ok: 'Stock OK',
    low: 'Stock bas',
    overdue: 'Stock probablement épuisé'
  };

  constructor(
    private readonly http: HttpClient,
    private readonly apiUrl: ApiUrlService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.checkProfileCompletion();
    this.loadHome();
    this.loadLastPurchase();
  }

  loadHome(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.http
      .get<HomeResponse>(this.apiUrl.build('/api/plateau/home'))
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.home = response;
          this.cdr.markForCheck();
        },
        error: () => {
          this.errorMessage = 'Impossible de charger les informations du plateau.';
          this.cdr.markForCheck();
        }
      });
  }

  requestConfirmPurchase(): void {
    if (!this.home?.actions?.canConfirmPurchase) return;
    this.showConfirmPurchase = true;
  }

  cancelConfirmPurchase(): void {
    this.showConfirmPurchase = false;
  }

  requestConfirmWarning(): void {
    this.showConfirmWarning = true;
  }

  cancelConfirmWarning(): void {
    this.showConfirmWarning = false;
  }

  confirmPurchase(): void {
    this.showConfirmPurchase = false;
    if (!this.home?.actions?.canConfirmPurchase) {
      return;
    }
    this.isConfirming = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.http
      .post<HomeResponse>(this.apiUrl.build('/api/plateau/purchases/confirm'), {})
      .pipe(
        finalize(() => {
          this.isConfirming = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.home = response;
          this.successMessage = 'Achat confirmé. Merci !';
          this.loadLastPurchase();
          this.cdr.markForCheck();
        },
        error: error => {
          if (error?.status === 409) {
            this.errorMessage = 'Quelqu’un a déjà confirmé. Rafraîchissement en cours...';
            this.loadHome();
            this.loadLastPurchase();
            this.cdr.markForCheck();
            return;
          }
          if (error?.status === 403) {
            this.errorMessage = 'Tu ne peux confirmer que si tu es responsable actuel.';
            this.cdr.markForCheck();
            return;
          }
          this.errorMessage = 'Impossible de confirmer l’achat.';
          this.cdr.markForCheck();
        }
      });
  }

  sendWarning(): void {
    this.showConfirmWarning = false;
    this.isWarningSending = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.http
      .post<void>(this.apiUrl.build('/api/plateau/warnings'), {})
      .pipe(
        finalize(() => {
          this.isWarningSending = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.successMessage = 'Warning envoyé au responsable actuel.';
          this.cdr.markForCheck();
        },
        error: error => {
          if (error?.status === 409) {
            this.errorMessage = 'Aucun responsable éligible pour le moment.';
            this.cdr.markForCheck();
            return;
          }
          if (error?.status === 429) {
            this.errorMessage = 'Warning déjà envoyé récemment, réessaye plus tard.';
            this.cdr.markForCheck();
            return;
          }
          this.errorMessage = 'Impossible d’envoyer le warning.';
          this.cdr.markForCheck();
        }
      });
  }

  loadLastPurchase(): void {
    this.isLastPurchaseLoading = true;
    this.http
      .get<PurchaseHistoryItem | null>(this.apiUrl.build('/api/plateau/purchases/last'))
      .pipe(
        finalize(() => {
          this.isLastPurchaseLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.lastPurchase = response ?? null;
          this.cdr.markForCheck();
        },
        error: () => {
          this.lastPurchase = null;
          this.cdr.markForCheck();
        }
      });
  }

  isCurrentResponsibleTag(item: PlanningItem, index: number): boolean {
    if (!this.home?.currentResponsible) {
      return false;
    }
    if (item.userId !== this.home.currentResponsible.userId) {
      return false;
    }
    return !this.home.planningPreview.slice(0, index).some(previous => previous.userId === item.userId);
  }

  openHistoryModal(): void {
    this.isHistoryOpen = true;
    this.loadHistory(true);
  }

  closeHistoryModal(): void {
    this.isHistoryOpen = false;
  }

  loadHistory(reset: boolean): void {
    if (reset) {
      this.isHistoryLoading = true;
      this.historyHasMore = true;
      this.historyPurchases = [];
      this.historyErrorMessage = '';
    } else {
      this.isHistoryLoadingMore = true;
    }
    const offset = this.historyPurchases.length;
    this.http
      .get<PurchaseHistoryResponse>(
        this.apiUrl.build(`/api/plateau/purchases/history?limit=${this.historyLimit}&offset=${offset}`)
      )
      .pipe(
        finalize(() => {
          this.isHistoryLoading = false;
          this.isHistoryLoadingMore = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.historyPurchases = [...this.historyPurchases, ...response.purchases];
          if (response.purchases.length < this.historyLimit) {
            this.historyHasMore = false;
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.historyErrorMessage = 'Impossible de charger l’historique.';
          this.cdr.markForCheck();
        }
      });
  }

  getResponsibleTooltip(item: PlanningItem): string {
    const tasses = item.tassesParJour ?? 0;
    const jours = item.joursPresentielParSemaine ?? 0;
    const tassesLabel = tasses === 1 ? 'tasse/jour' : 'tasses/jour';
    const joursLabel = jours === 1 ? 'jour en présentiel' : 'jours en présentiel';
    return `${tasses} ${tassesLabel} · ${jours} ${joursLabel}`;
  }

  getStockStatus(): StockStatus {
    return this.home?.actions?.stockStatus ?? 'ok';
  }

  getStockLabel(): string {
    return this.stockStatusLabels[this.getStockStatus()];
  }

  getStockTagVariant(): 'red' | 'green' {
    return this.getStockStatus() === 'ok' ? 'green' : 'red';
  }

  shouldShowStockAlert(): boolean {
    return this.getStockStatus() !== 'ok';
  }

  getStockAlertTitle(): string {
    return this.getStockLabel();
  }

  getStockAlertMessage(): string {
    return this.getStockStatus() === 'overdue'
      ? 'La date suggérée est dépassée : le stock est probablement épuisé.'
      : 'Le stock de café est bas. N’oubliez pas de racheter !';
  }

  private checkProfileCompletion(): void {
    this.http.get<MeResponse>(this.apiUrl.build('/api/me')).subscribe({
      next: response => {
        if (response.profil.tassesParJour === 0 || response.profil.joursPresentielParSemaine === 0) {
          this.router.navigateByUrl('/me');
        }
      }
    });
  }
}

interface HomeResponse {
  plateau: {
    id: number;
    code: string;
    etage: number | null;
    batiment: string | null;
  };
  stats: {
    nbBuveursActifs: number;
    mugsParPaquet: number;
    tassesParJourPlateau: number;
    joursParPaquet: number | null;
  };
  currentResponsible: HomeUser | null;
  nextResponsible: HomeUser | null;
  planningPreview: PlanningItem[];
  actions: {
    canConfirmPurchase: boolean;
    canSendWarning: boolean;
    stockStatus: StockStatus;
  };
}

type StockStatus = 'ok' | 'low' | 'overdue';

interface HomeUser {
  userId: number;
  prenom: string;
  nom: string;
  email: string;
}

interface PlanningItem {
  userId: number;
  prenom: string;
  nom: string;
  isCurrentUser: boolean;
  suggestedPurchaseAt: string | null;
  tassesParJour: number;
  joursPresentielParSemaine: number;
}

interface PurchaseHistoryItem {
  purchasedAt: string;
  buyer: HomeUser;
  commentaire: string | null;
}

interface PurchaseHistoryResponse {
  purchases: PurchaseHistoryItem[];
}

interface MeResponse {
  profil: {
    tassesParJour: number;
    joursPresentielParSemaine: number;
  };
}
